import { useCallback, useEffect, useRef, useState } from 'react';
import { analyticsService } from '../services/analyticsService';
import { readPinLayout } from '../utils/pinLayout';

function layoutPayload(items) {
  return items.map((item) => {
    const isTable = item.report?.query_spec?.visualization?.mode === 'table' || item.preview?.result?.kind === 'table';
    const layout = readPinLayout(item.pin, isTable ? 3 : 1);
    return {
      report_id: item.report?.report_id,
      col_span: layout.colSpan,
      row_span: layout.rowSpan,
    };
  }).filter((item) => item.report_id);
}

export default function useDashboardPins(orgId) {
  const [pinned, setPinned] = useState([]);
  const [saved, setSaved] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const pinnedRef = useRef(pinned);
  pinnedRef.current = pinned;

  const load = useCallback(async () => {
    if (!orgId) {
      setPinned([]);
      setSaved([]);
      return;
    }
    setIsLoading(true);
    const res = await analyticsService.getDashboard(orgId);
    if (res.ok) {
      setPinned(res.data?.pinned || []);
      setSaved(res.data?.saved || []);
    }
    setIsLoading(false);
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const pinReport = useCallback(
    async (reportId) => {
      const res = await analyticsService.pinReport(reportId, orgId);
      if (res.ok) await load();
      return res;
    },
    [orgId, load]
  );

  const persistLayout = useCallback(
    async (next) => {
      const previous = pinnedRef.current;
      setPinned(next);
      const res = await analyticsService.reorderPins(layoutPayload(next), orgId);
      if (!res.ok) setPinned(previous);
      return res;
    },
    [orgId]
  );

  const reorderPins = useCallback(
    async (reportIds) => {
      const current = pinnedRef.current;
      const byId = new Map(current.map((item) => [item.report?.report_id, item]));
      const next = reportIds.map((id) => byId.get(id)).filter(Boolean);
      const leftovers = current.filter((item) => !reportIds.includes(item.report?.report_id));
      return persistLayout(next.concat(leftovers));
    },
    [persistLayout]
  );

  const resizePin = useCallback(
    async (reportId, size) => {
      const next = pinnedRef.current.map((item) => {
        if (item.report?.report_id !== reportId) return item;
        return {
          ...item,
          pin: {
            ...(item.pin || {}),
            col_span: size.colSpan,
            row_span: size.rowSpan,
          },
        };
      });
      return persistLayout(next);
    },
    [persistLayout]
  );

  const unpinReport = useCallback(
    async (reportId) => {
      const res = await analyticsService.unpinReport(reportId, orgId);
      if (res.ok) await load();
      return res;
    },
    [orgId, load]
  );

  const deleteReport = useCallback(
    async (reportId) => {
      const res = await analyticsService.deleteSavedReport(reportId, orgId);
      if (res.ok) await load();
      return res;
    },
    [orgId, load]
  );

  return {
    pinned,
    saved,
    isLoading,
    reload: load,
    pinReport,
    reorderPins,
    resizePin,
    unpinReport,
    deleteReport,
  };
}
